/**
 * Browser-side client for the leads-service Person identity API (CLAUDE-16).
 *
 * The leads-service `/api/v1/persons/upsert` endpoint binds an authenticated
 * SFR3 user (`sfr3_user_id`) to a Person row keyed by phone, and threads
 * the browser's GA4 identity baton (`local_uuid`) onto the same record.
 * Once bound, server-side GA4 events fired from aapm-webhook can stitch
 * cross-device sessions back to the canonical Person.
 *
 * IMPORTANT: leads-service requires an internal API key on the `X-API-Key`
 * header. We do NOT expose that key to the browser — `bindPersonToUser`
 * POSTs to our own Next.js API route (`/api/leads/persons/upsert`) which
 * proxies the request server-side with the key from process.env.
 *
 * Best-effort: failures are swallowed and logged with `console.warn`. The
 * caller (resident-portal OTP flow) MUST NOT block on this — identity
 * stitching is an analytics concern, not a business-flow concern.
 *
 * Phone is sent in the request body, never logged. Same PII discipline as
 * leads-service itself.
 */

type BindPersonToUserBody = {
  phone: string;
  sfr3_user_id: string;
  local_uuid: string;
};

export async function bindPersonToUser(
  phone: string,
  sfr3_user_id: string,
  local_uuid: string
): Promise<void> {
  // Guard: skip if any required identifier is missing. We don't want to
  // create a Person row with empty fields just because the JWT decode
  // or local_uuid read happened to fail.
  if (!(phone && sfr3_user_id && local_uuid)) {
    return;
  }

  const body: BindPersonToUserBody = { phone, sfr3_user_id, local_uuid };

  try {
    const response = await fetch('/api/leads/persons/upsert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    // `fetch` only rejects on network errors. HTTP errors (401, 500, 502)
    // resolve normally — surface them here so misconfigured envs and
    // upstream outages show up in browser logs / Datadog RUM. Log the
    // status code ONLY; the response body can echo PII.
    if (!response.ok) {
      // biome-ignore lint/suspicious/noConsole: best-effort fire-and-forget telemetry; emit a single name-only warn.
      console.warn(
        '[bindPersonToUser] proxy non-2xx',
        `status=${response.status}`
      );
    }
  } catch (err) {
    // Best-effort. Do NOT throw — never block the OTP flow on a
    // failed analytics-stitching call. Don't log the body (PII).
    // biome-ignore lint/suspicious/noConsole: best-effort fire-and-forget telemetry; emit a single name-only warn.
    console.warn(
      '[bindPersonToUser] request failed',
      err instanceof Error ? err.name : 'unknown'
    );
  }
}
